import { get, ref as databaseRef } from 'firebase/database'
import database from 'services/firebase/database'

const RETRY_DELAYS = [1000, 3000, 10000]
const READ_TIMEOUT = 10000

// Firebase get() can fail or stay pending while offline. Keep each language's
// reader bounded and disposable because the translation package ignores cleanup.
const readTranslationsWithRecovery = ({ ref, setTranslations }) => {
  let disposed = false
  let pending = false
  let loaded = false
  let retries = 0
  let retryTimer
  let timeoutTimer

  const read = async () => {
    if (disposed || pending) return
    pending = true
    clearTimeout(retryTimer)
    try {
      const snapshot = await Promise.race([
        get(databaseRef(database, ref)),
        new Promise((resolve, reject) => {
          timeoutTimer = setTimeout(
            () => reject(new Error('Translation read timed out')),
            READ_TIMEOUT
          )
        })
      ])
      if (disposed) return
      const translations = snapshot.val()
      if (!translations || !Object.keys(translations).length) {
        throw new Error('Translations unavailable')
      }
      loaded = true
      setTranslations(translations)
    } catch (error) {
      if (!disposed && retries < RETRY_DELAYS.length) {
        retryTimer = setTimeout(read, RETRY_DELAYS[retries++])
      }
    } finally {
      clearTimeout(timeoutTimer)
      pending = false
    }
  }

  const recover = () => {
    if (disposed || loaded || pending) return
    retries = 0
    read()
  }

  // Clear the previous language; feature fallbacks remain readable during reads.
  setTranslations({})
  window.addEventListener('online', recover)
  window.addEventListener('focus', recover)
  read()

  return () => {
    disposed = true
    clearTimeout(retryTimer)
    clearTimeout(timeoutTimer)
    window.removeEventListener('online', recover)
    window.removeEventListener('focus', recover)
  }
}

export default readTranslationsWithRecovery
